/**
 * <transformation-timeline> — Transformation Timeline section behavior.
 *
 * Desktop (>= 750px): one expanded card + collapsed siblings. Clicking a
 * collapsed card or the prev/next arrows changes which card is expanded.
 * The size change is an instant layout snap (CSS drives it via .is-active);
 * outgoing track height is reserved with min-height + 2x rAF clear so content
 * below the section never jumps upward mid-swap.
 *
 * Mobile (< 750px): the track is a native scroll-snap carousel and the tab row
 * mirrors the card nearest the left edge. Tapping a tab or a card scrolls that
 * card into view. Scroll-position sync only runs on mobile — desktop scrolling
 * (possible when many cards overflow the page width) never steals the active
 * state from the merchant-chosen card.
 */
if (!customElements.get('transformation-timeline')) {
  customElements.define(
    'transformation-timeline',
    class TransformationTimeline extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onVideoIntersect = this.onVideoIntersect.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('[data-tt-track]');
        this.cards = Array.from(this.querySelectorAll('[data-tt-card]'));
        this.tabs = Array.from(this.querySelectorAll('[data-tt-tab]'));
        this.prevBtn = this.querySelector('[data-tt-prev]');
        this.nextBtn = this.querySelector('[data-tt-next]');
        this.liveRegion = this.querySelector('[data-tt-live]');
        this.activeIndex = Math.max(
          0,
          this.cards.findIndex(function (card) {
            return card.classList.contains('is-active');
          })
        );
        this.scrollTimer = null;
        this.addEventListener('click', this.onClick);
        if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.updateArrows();

        // One shared observer starts/stops looping card videos with viewport
        // visibility, so offscreen cards never download or decode video.
        var videos = this.querySelectorAll('[data-tt-video]');
        if (videos.length) {
          this.videoObserver = new IntersectionObserver(this.onVideoIntersect, { rootMargin: '100px' });
          videos.forEach(function (video) {
            this.videoObserver.observe(video);
          }, this);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        if (this.scrollTimer) clearTimeout(this.scrollTimer);
        if (this.videoObserver) {
          this.videoObserver.disconnect();
          this.videoObserver = null;
        }
      }

      onVideoIntersect(entries) {
        var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting && !reduced) {
            video.muted = true;
            var playAttempt = video.play();
            if (playAttempt && playAttempt.catch) playAttempt.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      }

      isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      motionOK() {
        return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      onClick(event) {
        var prev = event.target.closest('[data-tt-prev]');
        if (prev) return this.activate(this.activeIndex - 1, true);
        var next = event.target.closest('[data-tt-next]');
        if (next) return this.activate(this.activeIndex + 1, true);
        var tab = event.target.closest('[data-tt-tab]');
        if (tab) return this.activate(parseInt(tab.getAttribute('data-tt-index'), 10), true);
        var figure = event.target.closest('[data-tt-activate]');
        if (figure) {
          var card = figure.closest('[data-tt-card]');
          if (card) this.activate(parseInt(card.getAttribute('data-tt-index'), 10), true);
        }
      }

      onScroll() {
        // Tab sync is a mobile-carousel affordance only; on desktop the active
        // card is chosen by click/arrows, never by scroll position.
        if (!this.isMobile() || !this.track) return;
        if (this.scrollTimer) clearTimeout(this.scrollTimer);
        var self = this;
        this.scrollTimer = setTimeout(function () {
          var trackLeft = self.track.getBoundingClientRect().left;
          var best = 0;
          var bestDist = Infinity;
          self.cards.forEach(function (card, i) {
            var dist = Math.abs(card.getBoundingClientRect().left - trackLeft);
            if (dist < bestDist) {
              bestDist = dist;
              best = i;
            }
          });
          if (best !== self.activeIndex) self.activate(best, false);
        }, 120);
      }

      onBlockSelect(event) {
        var card = event.target && event.target.closest ? event.target.closest('[data-tt-card]') : null;
        if (!card || !this.contains(card)) return;
        this.activate(parseInt(card.getAttribute('data-tt-index'), 10), true);
      }

      activate(index, scrollTo) {
        if (!this.cards.length) return;
        index = Math.max(0, Math.min(index, this.cards.length - 1));
        if (index === this.activeIndex && !scrollTo) return;
        var changed = index !== this.activeIndex;
        var self = this;

        if (changed && this.track) {
          // Reserve outgoing height across the layout snap; clear once the new
          // layout has had a pass so the track settles to its natural height.
          // rAF is paused on hidden/background tabs, so a macrotask fallback
          // guarantees the reservation never outlives the swap.
          this.track.style.minHeight = this.track.offsetHeight + 'px';
          var clearReserve = function () {
            self.track.style.minHeight = '';
          };
          requestAnimationFrame(function () {
            requestAnimationFrame(clearReserve);
          });
          setTimeout(clearReserve, 250);
        }

        this.activeIndex = index;
        this.cards.forEach(function (card, i) {
          var isActive = i === index;
          card.classList.toggle('is-active', isActive);
          var btn = card.querySelector('[data-tt-activate]');
          if (btn) btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        });
        this.tabs.forEach(function (tab, i) {
          tab.classList.toggle('is-active', i === index);
          if (i === index) {
            tab.setAttribute('aria-current', 'true');
          } else {
            tab.removeAttribute('aria-current');
          }
        });
        this.updateArrows();

        if (changed && this.liveRegion) {
          var parts = [];
          var label = this.cards[index].querySelector('.transformation-timeline__label');
          var title = this.cards[index].querySelector('.transformation-timeline__title');
          if (label) parts.push(label.textContent.trim());
          if (title) parts.push(title.textContent.trim());
          this.liveRegion.textContent = parts.join(': ');
        }

        if (scrollTo) {
          this.cards[index].scrollIntoView({
            behavior: this.motionOK() ? 'smooth' : 'auto',
            block: 'nearest',
            inline: this.isMobile() ? 'start' : 'nearest'
          });
        }
      }

      updateArrows() {
        if (this.prevBtn) this.prevBtn.disabled = this.activeIndex <= 0;
        if (this.nextBtn) this.nextBtn.disabled = this.activeIndex >= this.cards.length - 1;
      }
    }
  );
}
